import { Link, useNavigate } from 'react-router-dom';
import { InvoiceListItem } from '../../api/types';
import { useGroups } from '../../api/useGroups';
import { useInvoiceDetail } from '../../api/useInvoiceDetail';
import { extractString, extractNumber } from '../../utils/sanitizeData';
import { getInvoiceQualitySummary } from '../../utils/dataQuality';

export interface LegacyInvoiceRecord {
  id: string;
  vendor?: string;
  vendor_name?: string | null;
  vendorInitial?: string;
  date?: string;
  invoice_date?: string | null;
  amount?: number;
  invoice_total?: { amount: string; currency: string } | null;
  currency?: 'USD' | 'EUR' | string;
  tags: Array<string | { name?: string; label?: string; description?: string }>;
  sharedWith?: string;
  sharing?: { is_shared: boolean; shared_with?: Array<string | number | { id?: string | number; name?: string; group_name?: string }> } | null;
  sharedCount?: number;
  issuesCount?: number;
  data_quality?: { issues_count?: number; issues?: unknown[] } | null;
  status: 'warning' | 'error' | 'clean' | string | number | { code: string | number; label: string; is_success?: boolean };
}

interface InvoiceHistoryTableProps {
  invoices?: (InvoiceListItem | LegacyInvoiceRecord)[];
  isLoading?: boolean;
  isError?: boolean;
  onSelectTag?: (tag: string) => void;
  selectedTag?: string | null;
}

const getVendorName = (inv: InvoiceListItem | LegacyInvoiceRecord | Record<string, unknown>): string => {
  const invObj = inv as Record<string, unknown>;
  if ('vendor_name' in invObj && invObj.vendor_name) {
    return extractString(invObj.vendor_name, 'Unknown Vendor');
  }
  if ('vendor' in invObj && invObj.vendor) {
    if (typeof invObj.vendor === 'object' && invObj.vendor !== null) {
      return extractString((invObj.vendor as Record<string, unknown>).name, 'Unknown Vendor');
    }
    return extractString(invObj.vendor, 'Unknown Vendor');
  }
  if ('fields' in invObj && typeof invObj.fields === 'object' && invObj.fields !== null) {
    const fieldsObj = invObj.fields as Record<string, unknown>;
    if (fieldsObj.vendor && typeof fieldsObj.vendor === 'object' && fieldsObj.vendor !== null) {
      return extractString((fieldsObj.vendor as Record<string, unknown>).name, 'Unknown Vendor');
    }
  }
  return 'Unknown Vendor';
};

const getVendorInitial = (inv: InvoiceListItem | LegacyInvoiceRecord | Record<string, unknown>): string => {
  const invObj = inv as Record<string, unknown>;
  if ('vendorInitial' in invObj && typeof invObj.vendorInitial === 'string' && invObj.vendorInitial) {
    return invObj.vendorInitial;
  }
  const name = getVendorName(inv);
  return name.charAt(0).toUpperCase() || 'I';
};

const getDate = (inv: InvoiceListItem | LegacyInvoiceRecord | Record<string, unknown>): string => {
  const invObj = inv as Record<string, unknown>;
  if ('invoice_date' in invObj && invObj.invoice_date) return extractString(invObj.invoice_date, '—');
  if ('date' in invObj && invObj.date) return extractString(invObj.date, '—');
  return '—';
};

const getAmount = (inv: InvoiceListItem | LegacyInvoiceRecord | Record<string, unknown>): string => {
  const invObj = inv as Record<string, unknown>;
  if (invObj.invoice_total && typeof invObj.invoice_total === 'object' && invObj.invoice_total !== null) {
    const amt = extractNumber((invObj.invoice_total as Record<string, unknown>).amount, 0);
    return amt.toFixed(2);
  }
  if ('amount' in invObj && typeof invObj.amount === 'number') {
    return invObj.amount.toFixed(2);
  }
  return '0.00';
};

const getCurrency = (inv: InvoiceListItem | LegacyInvoiceRecord | Record<string, unknown>): string => {
  const invObj = inv as Record<string, unknown>;
  if (invObj.invoice_total && typeof invObj.invoice_total === 'object' && invObj.invoice_total !== null) {
    const curr = (invObj.invoice_total as Record<string, unknown>).currency;
    if (curr && curr !== 'UNKNOWN') return extractString(curr, 'EUR');
  }
  if ('currency' in invObj && invObj.currency) {
    return extractString(invObj.currency, 'EUR');
  }
  return 'EUR';
};

const getTagLabel = (tag: unknown): string => {
  return extractString(tag, '');
};

const getSharingList = (
  inv: InvoiceListItem | LegacyInvoiceRecord | Record<string, unknown>,
  groupMap: Map<string, string>
): string[] => {
  const invObj = (inv as unknown) as Record<string, unknown>;
  const sharingObj = (invObj.sharing || {}) as Record<string, unknown>;

  const rawList =
    sharingObj.shared_with ||
    sharingObj.groups ||
    sharingObj.shared_groups ||
    sharingObj.organizations ||
    sharingObj.shared_organizations ||
    invObj.shared_with ||
    invObj.sharedWith ||
    invObj.shared_groups ||
    invObj.groups ||
    [];

  const list = Array.isArray(rawList) ? rawList : [rawList];

  const results = list
    .map((item) => {
      if (!item) return '';
      if (typeof item === 'string') {
        const trimmed = item.trim();
        return groupMap.get(trimmed) || groupMap.get(String(trimmed)) || trimmed;
      }
      if (typeof item === 'number') {
        return groupMap.get(String(item)) || `Group ${item}`;
      }
      if (typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        const candidateName =
          obj.name ||
          obj.group_name ||
          obj.title ||
          obj.description ||
          obj.label ||
          obj.organization_name;
        if (candidateName && typeof candidateName === 'string') {
          return candidateName;
        }
        const candidateId = obj.id || obj.group_id || obj.uuid || obj.organization_id;
        if (candidateId !== undefined && candidateId !== null) {
          return groupMap.get(String(candidateId)) || String(candidateId);
        }
      }
      return '';
    })
    .filter(Boolean);

  // Fallback if sharing is flagged true but no explicit group listed
  if (results.length === 0 && sharingObj.is_shared === true) {
    return ['Shared'];
  }

  return results;
};

interface InvoiceTableRowProps {
  inv: InvoiceListItem | LegacyInvoiceRecord;
  selectedTag?: string | null;
  onSelectTag?: (tag: string) => void;
  groupMap: Map<string, string>;
}

function InvoiceTableRow({
  inv,
  selectedTag,
  onSelectTag,
  groupMap,
}: InvoiceTableRowProps) {
  const navigate = useNavigate();
  // Fetch detailed data for this invoice (uses TanStack Query cache)
  const { data: detailData } = useInvoiceDetail(inv.id);
  const rawInvDq = (inv as unknown as Record<string, unknown>).data_quality;
  const rawDetailDq = detailData?.data_quality;
  const chosenDq =
    rawInvDq && typeof rawInvDq === 'object'
      ? rawInvDq
      : rawDetailDq && typeof rawDetailDq === 'object'
      ? rawDetailDq
      : undefined;

  const effectiveInvoice = {
    ...inv,
    ...(detailData || {}),
    ...(chosenDq ? { data_quality: chosenDq } : {}),
  };

  const vendor = getVendorName(effectiveInvoice);
  const initial = getVendorInitial(effectiveInvoice);
  const date = getDate(effectiveInvoice);
  const amount = getAmount(effectiveInvoice);
  const currency = getCurrency(effectiveInvoice);
  const sharingList = getSharingList(effectiveInvoice, groupMap);
  const qualitySummary = getInvoiceQualitySummary(effectiveInvoice);

  return (
    <tr
      key={String(inv.id || Math.random())}
      onClick={() => navigate(`/invoices/${inv.id}`)}
      className="hover:bg-brand-bg/80 dark:hover:bg-strokedark/50 transition-colors cursor-pointer group"
    >
      {/* Vendor Column */}
      <td className="py-3.5 px-5">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 font-bold text-xs border border-brand-vib/15 group-hover:border-brand-vib/40 transition-colors">
            {initial}
          </div>
          <div>
            <div className="font-bold text-brand-navy dark:text-white group-hover:text-brand-vib dark:group-hover:text-brand-light transition-colors">
              {vendor}
            </div>
            <div className="text-xs font-mono text-brand-soft dark:text-bodydark2">
              {date}
            </div>
          </div>
        </div>
      </td>

      {/* Total Column */}
      <td className="py-3.5 px-4 text-right font-mono font-bold text-brand-navy dark:text-white whitespace-nowrap">
        {amount}{' '}
        <span className="text-xs font-normal text-brand-soft">
          {currency}
        </span>
      </td>

      {/* Tags Column (Clickable filter) */}
      <td className="py-3.5 px-4">
        {effectiveInvoice.tags && effectiveInvoice.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {effectiveInvoice.tags.map((rawTag, tIdx) => {
              const tagLabel = getTagLabel(rawTag);
              const cleanLabel = tagLabel.replace(/^#/, '');
              const isSelected =
                selectedTag?.toLowerCase().replace(/^#/, '') === cleanLabel.toLowerCase();
              return (
                <button
                  key={tIdx}
                  type="button"
                  title={`Filter by tag #${cleanLabel}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectTag?.(cleanLabel);
                  }}
                  className={`px-2 py-0.5 rounded-md text-[10px] font-mono font-medium transition-all border cursor-pointer ${
                    isSelected
                      ? 'bg-brand-vib text-white border-brand-vib shadow-xs'
                      : 'bg-brand-bg hover:bg-brand-blue-tint dark:bg-strokedark dark:hover:bg-brand-900/40 text-brand-navy dark:text-white border-brand-line dark:border-strokedark hover:border-brand-vib/30'
                  }`}
                >
                  #{cleanLabel}
                </button>
              );
            })}
          </div>
        ) : (
          <span className="text-xs font-mono text-brand-soft dark:text-bodydark2 italic">
            No tags
          </span>
        )}
      </td>

      {/* Shared With Column (Clickable group filter) */}
      <td className="py-3.5 px-4">
        {sharingList.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {sharingList.map((groupName, gIdx) => {
              const cleanGroup = groupName.replace(/^#/, '');
              const isSelected =
                selectedTag?.toLowerCase().replace(/^#/, '') === cleanGroup.toLowerCase();
              return (
                <button
                  key={gIdx}
                  type="button"
                  title={`Filter by group ${cleanGroup}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectTag?.(cleanGroup);
                  }}
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-mono font-medium border cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-brand-vib text-white border-brand-vib shadow-xs'
                      : 'bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 border-brand-vib/15 hover:border-brand-vib/40'
                  }`}
                >
                  <svg className="w-3 h-3 fill-current opacity-70" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                  </svg>
                  <span>{cleanGroup}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <span className="text-xs font-mono text-brand-soft dark:text-bodydark2 italic">
            Private
          </span>
        )}
      </td>

      {/* Status / Issues Column */}
      <td className="py-3.5 px-4 text-center" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => navigate(`/invoices/${inv.id}/issues`)}
          title={
            qualitySummary.totalIssues > 0
              ? `${qualitySummary.totalIssues} issue(s) detected - Click to review`
              : 'All data quality checks passed - Click to review'
          }
          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-mono font-semibold transition-all cursor-pointer shadow-xs ${
            qualitySummary.badgeType === 'critical'
              ? 'bg-rose-500 text-white hover:bg-rose-600'
              : qualitySummary.badgeType === 'warning'
              ? 'bg-amber-500 text-white hover:bg-amber-600'
              : qualitySummary.badgeType === 'clean'
              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
              : 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200'
          }`}
        >
          {qualitySummary.badgeType === 'critical' && (
            <>
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              <span>{qualitySummary.totalIssues}</span>
            </>
          )}
          {qualitySummary.badgeType === 'warning' && (
            <>
              <span>⚠️</span>
              <span>{qualitySummary.totalIssues}</span>
            </>
          )}
          {qualitySummary.badgeType === 'clean' && (
            <span className="w-4 h-4 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold">
              ✓
            </span>
          )}
          {qualitySummary.badgeType === 'pending' && (
            <>
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              <span>Processing</span>
            </>
          )}
        </button>
      </td>

      {/* Actions Column */}
      <td className="py-3.5 px-5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <div className="inline-flex items-center gap-2">
          <Link
            to={`/invoices/${inv.id}`}
            className="px-3 py-1 rounded-lg text-xs font-semibold bg-brand-blue-tint text-brand-vib hover:bg-brand-vib hover:text-white dark:bg-brand-900/40 dark:text-brand-300 dark:hover:bg-brand-vib dark:hover:text-white transition-colors"
          >
            View
          </Link>
          <a
            href={`/api/v2/invoices/${inv.id}/download/`}
            download
            title="Download original document"
            className="p-1 text-brand-soft hover:text-brand-navy dark:hover:text-white transition-colors"
          >
            <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </a>
        </div>
      </td>
    </tr>
  );
}

export default function InvoiceHistoryTable({
  invoices,
  isLoading = false,
  isError = false,
  onSelectTag,
  selectedTag,
}: InvoiceHistoryTableProps) {
  // Fetch available sharing groups to resolve group UUIDs/IDs into human names
  const { data: groupsData } = useGroups();
  const groupMap = new Map<string, string>();
  if (groupsData && Array.isArray(groupsData)) {
    groupsData.forEach((g) => {
      const orgName =
        extractString(g.organization_name) ||
        (typeof g.organization === 'object' && g.organization !== null ? extractString(g.organization.name) : '') ||
        extractString(g.company_name) ||
        extractString(g.company);
      const displayLabel = orgName ? `${g.name} (${orgName})` : String(g.name);

      if (g.id !== undefined && g.id !== null && g.name) {
        groupMap.set(String(g.id), displayLabel);
      }
      if (g.name && !groupMap.has(String(g.name))) {
        groupMap.set(String(g.name), displayLabel);
      }
    });
  }

  // Real live invoices from API (no fake demo records)
  const baseInvoices = invoices || [];

  // Filter records by period and tag / sharing group
  const displayInvoices = baseInvoices.filter((inv) => {
    // 1. Tag or Sharing Group Filter
    if (selectedTag) {
      const cleanFilter = selectedTag.trim().toLowerCase().replace(/^#/, '');
      const hasTag = inv.tags?.some((t) => {
        const tName = extractString(t, '').trim().toLowerCase().replace(/^#/, '');
        return tName === cleanFilter || tName.includes(cleanFilter);
      });
      const sharingList = getSharingList(inv, groupMap);
      const hasGroup = sharingList.some((g) => {
        const gName = g.trim().toLowerCase().replace(/^#/, '');
        return gName === cleanFilter || gName.includes(cleanFilter);
      });

      if (!hasTag && !hasGroup) return false;
    }

    return true;
  });

  return (
    <div className="rounded-2.5xl border border-brand-line bg-white shadow-card dark:border-strokedark dark:bg-boxdark overflow-hidden">
      {/* Table Title Header */}
      <div className="p-4 sm:p-5 border-b border-brand-line dark:border-strokedark flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <h2 className="text-sm font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
            Invoice History ({displayInvoices.length})
          </h2>
          {isLoading && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 border border-brand-vib/20">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-vib animate-pulse" />
              Syncing…
            </span>
          )}
        </div>
        <span className="text-xs font-mono text-brand-soft dark:text-bodydark2">
          {isLoading && displayInvoices.length === 0 ? 'Fetching live records…' : isError ? 'Error loading records' : 'Live Records'}
        </span>
      </div>

      {/* Top Animated Loading Bar */}
      {isLoading && (
        <div className="h-0.5 w-full bg-brand-blue-tint dark:bg-brand-900/30 overflow-hidden">
          <div className="h-full bg-brand-vib w-full animate-pulse" />
        </div>
      )}

      {/* Desktop Table View */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-brand-blue-tint/60 dark:bg-brand-900/30 border-b border-brand-line dark:border-strokedark">
              <th className="py-3 px-5 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300">
                Vendor
              </th>
              <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 text-right">
                Total
              </th>
              <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300">
                Tags
              </th>
              <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300">
                Shared With
              </th>
              <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 text-center">
                Status
              </th>
              <th className="py-3 px-5 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-line dark:divide-strokedark text-sm">
            {isLoading && displayInvoices.length === 0 ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <tr key={idx} className="animate-pulse">
                  <td className="py-3.5 px-5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gray-200 dark:bg-strokedark flex-shrink-0" />
                      <div className="space-y-1.5">
                        <div className="h-3.5 bg-gray-200 dark:bg-strokedark rounded w-32" />
                        <div className="h-2.5 bg-gray-200 dark:bg-strokedark rounded w-20" />
                      </div>
                    </div>
                  </td>
                  <td className="py-3.5 px-4 text-right">
                    <div className="h-3.5 bg-gray-200 dark:bg-strokedark rounded w-16 ml-auto" />
                  </td>
                  <td className="py-3.5 px-4">
                    <div className="h-5 bg-gray-200 dark:bg-strokedark rounded-md w-24" />
                  </td>
                  <td className="py-3.5 px-4">
                    <div className="h-5 bg-gray-200 dark:bg-strokedark rounded-md w-20" />
                  </td>
                  <td className="py-3.5 px-4 text-center">
                    <div className="h-5 bg-gray-200 dark:bg-strokedark rounded-full w-20 mx-auto" />
                  </td>
                  <td className="py-3.5 px-5 text-right">
                    <div className="h-6 bg-gray-200 dark:bg-strokedark rounded-lg w-14 ml-auto" />
                  </td>
                </tr>
              ))
            ) : displayInvoices.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-12 px-5 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <svg className="w-8 h-8 text-brand-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm font-semibold text-brand-navy dark:text-white">No invoices found for this period</p>
                    <p className="text-xs text-brand-soft">There are no parsed invoices matching the selected dates or filter criteria.</p>
                    <Link
                      to="/upload"
                      className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover transition-colors shadow-sm"
                    >
                      + Upload First Invoice
                    </Link>
                  </div>
                </td>
              </tr>
            ) : (
              displayInvoices.map((inv) => (
                <InvoiceTableRow
                  key={String(inv.id || Math.random())}
                  inv={inv}
                  selectedTag={selectedTag}
                  onSelectTag={onSelectTag}
                  groupMap={groupMap}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* End of history footer notice */}
      <div className="py-4 text-center text-xs font-mono text-brand-soft dark:text-bodydark2 border-t border-brand-line dark:border-strokedark">
        — You&rsquo;ve reached the end of your invoice history —
      </div>
    </div>
  );
}
